<script setup>
import { computed, ref, watch } from "vue";
import { Document } from "@element-plus/icons-vue";
import SocialFundCallPeriodService from "@/api/services/socialFundCallPeriod.service";
import Notification from "@/utils/plugins/NotificationService";
import { useUiStore } from "@/app/stores/uiStore";

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  callPeriodId: { type: [Number, String], default: null },
  callPeriodLabel: { type: String, default: "" },
});

const emit = defineEmits(["update:modelValue"]);

const uiStore = useUiStore();
const history = ref([]);
const loading = ref(false);
const uploading = ref(false);

const visible = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const activeAgreement = computed(() => history.value.find((item) => item.active) || null);
// 2026-09-29: una convocatoria nunca puede quedarse sin convenio — el
// backend rechaza con 422 borrar el único que quede.
// 2026-09-30: además, un convenio ya usado en una notificación real enviada
// a un solicitante queda protegido, aunque ya no sea el vigente ni sea el
// único del histórico — el backend también lo rechaza con 422.
// Ambas condiciones se deshabilitan directamente en vez de dejar que el
// usuario choque con el error.
const canDelete = (item) => history.value.length > 1 && !item.used;
const deleteDisabledReason = (item) => {
  if (item.used) return "Este convenio ya se ha usado en una notificación enviada a un solicitante";
  if (history.value.length <= 1) return "Una convocatoria no puede quedarse sin convenio";
  return "";
};
const deletingId = ref(null);
const formatDateTime = (value) =>
  value ? new Date(value).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

const loadHistory = async () => {
  if (!props.callPeriodId) return;
  loading.value = true;
  try {
    const res = await SocialFundCallPeriodService.getAgreementHistory(props.callPeriodId);
    history.value = Array.isArray(res?.data) ? res.data : [];
  } finally {
    loading.value = false;
  }
};

watch(
  () => props.modelValue,
  (isVisible) => {
    if (isVisible) loadHistory();
  }
);

const close = () => {
  if (uploading.value) return;
  visible.value = false;
};

const onFileSelected = async (event) => {
  const file = event.target.files[0];
  event.target.value = "";
  if (!file) return;

  uploading.value = true;
  uiStore.startLoading();
  try {
    await SocialFundCallPeriodService.uploadAgreement(props.callPeriodId, file);
    Notification.success("Convenio subido correctamente.", 3500);
    await loadHistory();
  } catch (err) {
    const message = err?.response?.data?.error || "No se ha podido subir el convenio.";
    Notification.addNotification(message, "error", 6000);
  } finally {
    uploading.value = false;
    uiStore.stopLoading();
  }
};

const deleteAgreement = async (document) => {
  deletingId.value = document.id;
  uiStore.startLoading();
  try {
    await SocialFundCallPeriodService.deleteAgreement(props.callPeriodId, document.id);
    Notification.success("Convenio eliminado correctamente.", 3500);
    await loadHistory();
  } catch (err) {
    const message = err?.response?.data?.error || "No se ha podido eliminar el convenio.";
    Notification.addNotification(message, "error", 6000);
  } finally {
    deletingId.value = null;
    uiStore.stopLoading();
  }
};

const downloadAgreement = async (document) => {
  try {
    const response = await SocialFundCallPeriodService.downloadAgreement(props.callPeriodId, document.id);
    const blob = new Blob([response.data]);
    const link = window.document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = document.originalName || `convenio-${document.id}.pdf`;
    link.click();
    URL.revokeObjectURL(link.href);
  } catch {
    Notification.addNotification("Error al descargar el convenio.", "error", 6000);
  }
};
</script>

<template>
  <el-dialog v-model="visible" width="560px" :show-close="false" :lock-scroll="true"
    class="social-fund-agreement-dialog">
    <section v-if="visible" class="social-fund-agreement-dialog__content">
      <img class="social-fund-agreement-dialog__close pointer" src="@/assets/images/aspa_blue_small_entrada.png"
        alt="Cerrar" :class="{ 'social-fund-agreement-dialog__close--disabled': uploading }" @click="close" />

      <header class="social-fund-agreement-dialog__header">
        <el-icon>
          <Document />
        </el-icon>
        <div>
          <h2>Convenio</h2>
          <p>{{ callPeriodLabel }}</p>
        </div>
      </header>

      <label class="social-fund-agreement-dialog__upload-button"
        :class="{ 'social-fund-agreement-dialog__upload-button--disabled': uploading }">
        {{ uploading ? "SUBIENDO..." : (activeAgreement ? "SUSTITUIR CONVENIO VIGENTE" : "SUBIR CONVENIO") }}
        <input type="file" accept="application/pdf" class="social-fund-agreement-dialog__file-input"
          :disabled="uploading" @change="onFileSelected" />
      </label>

      <h3 class="social-fund-agreement-dialog__history-title">Histórico de versiones</h3>
      <p v-if="loading" class="social-fund-agreement-dialog__empty">Cargando...</p>
      <p v-else-if="!history.length" class="social-fund-agreement-dialog__empty">Todavía no se ha subido ningún convenio.</p>
      <ul v-else class="social-fund-agreement-dialog__history-list">
        <li v-for="item in history" :key="item.id" class="social-fund-agreement-dialog__history-row">
          <span class="social-fund-agreement-dialog__history-name">
            <span class="social-fund-agreement-dialog__history-filename">{{ item.originalName }}</span>
            <span v-if="item.active" class="social-fund-agreement-dialog__active-badge">Vigente</span>
            <span v-if="item.used" class="social-fund-agreement-dialog__used-badge">Usado</span>
          </span>
          <span class="social-fund-agreement-dialog__history-date">{{ formatDateTime(item.createdAt) }}</span>
          <button type="button" class="social-fund-agreement-dialog__download-button"
            @click="downloadAgreement(item)">DESCARGAR</button>
          <button type="button" class="social-fund-agreement-dialog__delete-button"
            :disabled="!canDelete(item) || deletingId === item.id" :title="deleteDisabledReason(item)"
            @click="deleteAgreement(item)">ELIMINAR</button>
        </li>
      </ul>

      <footer class="social-fund-agreement-dialog__actions">
        <button type="button" class="btn btn_yellow" :disabled="uploading" @click="close">CERRAR</button>
      </footer>
    </section>
  </el-dialog>
</template>

<style lang="scss" scoped>
:global(.el-dialog.social-fund-agreement-dialog) {
  background: #f5f3ff;
  border-radius: 20px;
  max-width: calc(100vw - 24px);
  overflow: hidden;
}

.social-fund-agreement-dialog :deep(.el-dialog__header) {
  display: none;
}

.social-fund-agreement-dialog :deep(.el-dialog__body) {
  padding: 0;
}

.social-fund-agreement-dialog__content {
  color: #263052;
  padding: 8px 16px 14px;
  position: relative;
}

.social-fund-agreement-dialog__close {
  height: 38px;
  object-fit: contain;
  position: absolute;
  right: 12px;
  top: 8px;
  width: 38px;
}

.social-fund-agreement-dialog__close--disabled {
  cursor: not-allowed;
  opacity: .5;
}

.social-fund-agreement-dialog__header {
  align-items: center;
  display: flex;
  gap: 12px;
  padding-right: 48px;
}

.social-fund-agreement-dialog__header > .el-icon {
  color: $bg-blue;
  font-size: 31px;
}

.social-fund-agreement-dialog__header h2 {
  color: $bg-blue;
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -.04em;
  line-height: 1.1;
  margin: 0;
}

.social-fund-agreement-dialog__header p {
  color: #737b94;
  font-size: 13px;
  margin: 4px 0 0;
}

.social-fund-agreement-dialog__upload-button {
  align-items: center;
  background: #f5f3ff;
  border: 1px solid $bg-blue;
  border-radius: 20px;
  color: $bg-blue;
  cursor: pointer;
  display: inline-flex;
  font-size: 12px;
  font-weight: 800;
  justify-content: center;
  margin-top: 20px;
  padding: 10px 18px;
  position: relative;
  width: 100%;
}

.social-fund-agreement-dialog__upload-button--disabled {
  cursor: not-allowed;
  opacity: .5;
}

.social-fund-agreement-dialog__file-input {
  height: 1px;
  opacity: 0;
  position: absolute;
  width: 1px;
}

.social-fund-agreement-dialog__history-title {
  color: #27304f;
  font-size: 15px;
  font-weight: 800;
  margin: 22px 0 10px;
}

.social-fund-agreement-dialog__empty {
  color: #737b94;
  font-size: 13px;
}

.social-fund-agreement-dialog__history-list {
  border: 1px solid #dde0e9;
  border-radius: 9px;
  list-style: none;
  margin: 0;
  overflow: hidden;
  padding: 0;
}

.social-fund-agreement-dialog__history-row {
  align-items: center;
  border-bottom: 1px solid #e8eaf0;
  display: grid;
  font-size: 12px;
  gap: 10px;
  grid-template-columns: minmax(0, 1fr) auto auto;
  padding: 10px 12px;
}

.social-fund-agreement-dialog__history-row:last-child {
  border-bottom: 0;
}

.social-fund-agreement-dialog__history-name {
  align-items: center;
  color: #27304f;
  display: flex;
  flex-wrap: wrap;
  font-weight: 700;
  gap: 8px;
  grid-column: 1 / -1;
  min-width: 0;
}

.social-fund-agreement-dialog__history-filename {
  flex-basis: 100%;
  line-height: 1.5;
  min-width: 0;
  overflow-wrap: anywhere;
}

.social-fund-agreement-dialog__active-badge {
  background: #ebfff0;
  border-radius: 12px;
  color: #238a43;
  font-size: 10px;
  font-weight: 800;
  padding: 2px 8px;
  white-space: nowrap;
}

.social-fund-agreement-dialog__used-badge {
  background: #eef0f8;
  border-radius: 12px;
  color: #4d5570;
  font-size: 10px;
  font-weight: 800;
  padding: 2px 8px;
  white-space: nowrap;
}

.social-fund-agreement-dialog__history-date {
  color: #737b94;
  white-space: nowrap;
}

.social-fund-agreement-dialog__download-button {
  background: #fff;
  border: 1px solid $bg-blue;
  border-radius: 4px;
  color: $bg-blue;
  font-size: 9px;
  font-weight: 800;
  padding: 3px 8px;
  white-space: nowrap;
}

.social-fund-agreement-dialog__delete-button {
  background: #fff;
  border: 1px solid #f9156f;
  border-radius: 4px;
  color: #f9156f;
  font-size: 9px;
  font-weight: 800;
  padding: 3px 8px;
  white-space: nowrap;
}

.social-fund-agreement-dialog__delete-button:disabled {
  cursor: not-allowed;
  opacity: .4;
}

.social-fund-agreement-dialog__actions {
  border-top: 1px solid #e2e5ed;
  display: flex;
  gap: 14px;
  justify-content: flex-end;
  margin: 20px -16px -14px;
  padding: 17px 16px;
}

.social-fund-agreement-dialog__actions .btn {
  align-items: center;
  display: inline-flex;
  font-weight: 800;
  justify-content: center;
  margin: 0 !important;
  min-height: 42px;
  min-width: 160px;
}

.social-fund-agreement-dialog__actions .btn_yellow {
  color: $bg-blue !important;
}

@media (max-width: 767px) {
  :global(.el-dialog.social-fund-agreement-dialog) {
    max-height: calc(100vh - 24px);
  }

  .social-fund-agreement-dialog :deep(.el-dialog__body) {
    max-height: calc(100vh - 24px);
    overflow-y: auto;
  }

  .social-fund-agreement-dialog__content {
    padding: 18px 16px 14px;
  }

  .social-fund-agreement-dialog__history-row {
    grid-template-columns: minmax(0, 1fr);
  }

  .social-fund-agreement-dialog__actions {
    padding: 14px 16px;
  }

  .social-fund-agreement-dialog__actions .btn {
    width: 100%;
  }
}
</style>
